import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { apiClient } from '../services/apiClient';
import {
  sha256Browser,
  executeAndVerifyLotteryBrowser,
  BrowserLotteryResult,
} from '../utils/browserCrypto';
import {
  Dice5,
  ShieldCheck,
  Key,
  Copy,
  Check,
  Download,
  CheckCircle2,
  AlertTriangle,
  Play,
  RotateCcw,
  Sparkles,
  Users,
  Lock,
  Layers,
} from 'lucide-react';

export const LotteryPortal: React.FC = () => {
  const { user, member, isStaff } = useAuth();
  const { t } = useTranslation();

  const [activeLottery, setActiveLottery] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Member Commitment Form State
  const [generatedSalt, setGeneratedSalt] = useState<string>('');
  const [computedCommitment, setComputedCommitment] = useState<string>('');
  const [hasCopiedVoucher, setHasCopiedVoucher] = useState<boolean>(false);
  const [submittingCommitment, setSubmittingCommitment] = useState<boolean>(false);
  const [commitmentSuccess, setCommitmentSuccess] = useState<boolean>(false);

  // Admin Execution State
  const [adminSeedInput, setAdminSeedInput] = useState<string>('');
  const [adminCommitmentInput, setAdminCommitmentInput] = useState<string>('');
  const [initiating, setInitiating] = useState<boolean>(false);
  const [executing, setExecuting] = useState<boolean>(false);
  const [revealedSaltsJson, setRevealedSaltsJson] = useState<string>('');

  // Local Re-verification State
  const [localVerification, setLocalVerification] = useState<{
    tested: boolean;
    valid: boolean;
    result?: BrowserLotteryResult;
    error?: string;
  } | null>(null);

  const fetchLotteryState = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(collection(db, 'lottery'), orderBy('createdAt', 'desc'), limit(1));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        setActiveLottery({ id: doc.id, ...doc.data() });
      } else {
        setActiveLottery(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch lottery:', err);
      setError(err.message || 'Failed to fetch lottery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLotteryState();
  }, []);

  // Generate 32-byte secure random hex salt
  const generateRandomSalt = async () => {
    const randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);
    const hexSalt = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    setGeneratedSalt(hexSalt);

    const hash = await sha256Browser(hexSalt);
    setComputedCommitment(hash);
    setHasCopiedVoucher(false);
  };

  const downloadSaltVoucher = () => {
    const text = `FUTURE HOME PLAN FOUNDATION (FHPF) - LOTTERY SALT VOUCHER\n` +
      `Date: ${new Date().toISOString()}\n` +
      `Member UID: ${user?.uid}\n` +
      `Member Name: ${member?.name || 'FHPF Member'}\n` +
      `Secret 32-Byte Salt (Keep Safe!): ${generatedSalt}\n` +
      `Public Commitment SHA-256: ${computedCommitment}\n` +
      `\nNOTE: You must reveal this secret salt during the live lottery event to calculate the final draw seed.`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fhpf-lottery-voucher-${user?.uid}.txt`;
    a.click();
    setHasCopiedVoucher(true);
  };

  const submitMemberCommitment = async () => {
    if (!activeLottery || !computedCommitment) return;
    try {
      setSubmittingCommitment(true);
      setError(null);

      await apiClient.post('/lottery/commit', {
        lotteryId: activeLottery.id,
        commitment: computedCommitment,
      });

      setCommitmentSuccess(true);
      await fetchLotteryState();
    } catch (err: any) {
      setError(err.message || 'Failed to submit commitment');
    } finally {
      setSubmittingCommitment(false);
    }
  };

  const initiateNewLottery = async () => {
    if (!adminCommitmentInput) return;
    try {
      setInitiating(true);
      setError(null);

      await apiClient.post('/lottery/create', {
        adminSeedCommitment: adminCommitmentInput,
      });

      await fetchLotteryState();
    } catch (err: any) {
      setError(err.message || 'Failed to initiate lottery');
    } finally {
      setInitiating(false);
    }
  };

  const executeLotteryDraw = async () => {
    if (!activeLottery || !adminSeedInput) return;
    try {
      setExecuting(true);
      setError(null);

      let parsedSalts = {};
      try {
        parsedSalts = JSON.parse(revealedSaltsJson);
      } catch (e) {
        throw new Error('Revealed member salts must be valid JSON: {"memberId": "salt", ...}');
      }

      await apiClient.post('/lottery/reveal', {
        lotteryId: activeLottery.id,
        revealedAdminSeed: adminSeedInput,
        revealedMemberSalts: parsedSalts,
      });

      await fetchLotteryState();
    } catch (err: any) {
      setError(err.message || 'Failed to execute draw');
    } finally {
      setExecuting(false);
    }
  };

  const reverifyLocally = async () => {
    if (!activeLottery || activeLottery.status !== 'COMPLETED') return;
    try {
      const result = await executeAndVerifyLotteryBrowser({
        lotteryId: activeLottery.lotteryId,
        eligibleMemberIds: activeLottery.eligibleMemberIds,
        flatNumbers: activeLottery.flatNumbers,
        adminSeedCommitment: activeLottery.adminSeedCommitment,
        revealedAdminSeed: activeLottery.revealedAdminSeed,
        memberCommitments: activeLottery.memberCommitments,
        revealedMemberSalts: activeLottery.revealedMemberSalts,
      });

      setLocalVerification({
        tested: true,
        valid: result.valid,
        result: result.result,
        error: result.error,
      });
    } catch (err: any) {
      setLocalVerification({
        tested: true,
        valid: false,
        error: err.message,
      });
    }
  };

  const commitmentsMap = activeLottery?.memberCommitments || {};
  const totalEligible = activeLottery?.eligibleMemberIds?.length || 0;
  const totalCommitted = Object.keys(commitmentsMap).length;
  const userHasCommitted = user && commitmentsMap[user.uid];

  return (
    <div className="space-y-6 pb-24 sm:pb-8">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-navy-900 to-navy-950 text-white border border-navy-800 shadow-md">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">
            <Dice5 className="w-4 h-4" />
            <span>Multi-Party Cryptographic Protocol</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">{t('lottery.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{t('lottery.subtitle')}</p>
        </div>

        {activeLottery && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-navy-800 border border-navy-700 text-xs font-semibold text-amber-300">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>{activeLottery.status}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-xs sm:text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Protocol Explainer */}
      <div className="p-4 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 text-xs sm:text-sm space-y-2">
        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-primary-500" />
          <span>How the Multi-Party Commitment-and-Reveal Protocol Guarantees 100% Fairness</span>
        </h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
          1. <strong>Frozen List:</strong> The 20 eligible members are frozen and hashed into <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-navy-800 font-mono text-2xs">H_members</code>.
          <br />
          2. <strong>Secret Commitments:</strong> Each member generates a secret 32-byte salt <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-navy-800 font-mono text-2xs">s_i</code> and sends only its hash <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-navy-800 font-mono text-2xs">SHA-256(s_i)</code> before the deadline. The admin commits <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-navy-800 font-mono text-2xs">SHA-256(S_admin)</code>.
          <br />
          3. <strong>Public Reveal & Final Seed:</strong> All salts are revealed simultaneously during a live session. The system verifies every commitment, combines all entropy via HMAC-SHA256, and executes a deterministic Fisher-Yates shuffle. <em>No single individual can predict or bias the flat allocation.</em>
        </p>
      </div>

      {/* Active Phase & Member Participation */}
      {activeLottery && activeLottery.status === 'COMMITMENT_PHASE' && (
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {t('lottery.phaseCommitment')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Collection deadline active. Please submit your secret commitment.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {totalCommitted} of {totalEligible} Members Committed
                </span>
                <div className="w-24 bg-gray-200 dark:bg-navy-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary-500 h-2 transition-all"
                    style={{ width: `${totalEligible > 0 ? (totalCommitted / totalEligible) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Member Participation Area */}
            {userHasCommitted ? (
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200 text-xs sm:text-sm flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <div>
                  <div className="font-bold">Your commitment is securely registered!</div>
                  <div className="text-xs opacity-80 mt-0.5 font-mono break-all">
                    Commitment: {commitmentsMap[user?.uid || '']}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-gray-50 dark:bg-navy-950/60 border border-gray-200 dark:border-navy-800 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Step 1: Generate & Save Your Secret Salt
                </h4>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={generateRandomSalt}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-primary-600 hover:bg-primary-500 text-white transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{t('lottery.generateSalt')}</span>
                  </button>

                  {generatedSalt && (
                    <button
                      onClick={downloadSaltVoucher}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>{t('lottery.copyVoucher')}</span>
                    </button>
                  )}
                </div>

                {generatedSalt && (
                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-gray-500 font-medium">Your 32-Byte Secret Salt:</span>
                      <code className="block p-2 rounded bg-white dark:bg-navy-900 font-mono text-2xs break-all border border-gray-200 dark:border-navy-800 text-gray-800 dark:text-gray-200">
                        {generatedSalt}
                      </code>
                    </div>

                    <div>
                      <span className="text-gray-500 font-medium">Calculated Commitment (SHA-256):</span>
                      <code className="block p-2 rounded bg-primary-50 dark:bg-primary-950/40 font-mono text-2xs break-all border border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-300">
                        {computedCommitment}
                      </code>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={submitMemberCommitment}
                        disabled={submittingCommitment || !hasCopiedVoucher}
                        className={`px-4 py-2 rounded-xl text-xs font-bold text-white transition-all ${
                          hasCopiedVoucher
                            ? 'bg-primary-600 hover:bg-primary-500 shadow-md shadow-primary-600/30'
                            : 'bg-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {submittingCommitment ? 'Registering...' : t('lottery.submitCommitmentBtn')}
                      </button>
                      {!hasCopiedVoucher && (
                        <span className="block text-2xs text-amber-500 mt-1 font-medium">
                          Please download or backup your salt voucher first before submitting.
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Completed Draw Results Visualization */}
      {activeLottery && activeLottery.status === 'COMPLETED' && (
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 text-emerald-500 text-xs font-bold uppercase tracking-wider">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Public Lottery Draw Concluded & Cryptographically Sealed</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
                  Official Flat Distribution Allocations
                </h3>
              </div>

              <button
                onClick={reverifyLocally}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-primary-600 hover:bg-primary-500 text-white transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>{t('lottery.reverifyBtn')}</span>
              </button>
            </div>

            {/* Local verification badge */}
            {localVerification && (
              <div
                className={`p-3 rounded-xl border text-xs ${
                  localVerification.valid
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 text-emerald-900 dark:text-emerald-200'
                    : 'bg-red-50 dark:bg-red-950/40 border-red-300 text-red-900 dark:text-red-200'
                }`}
              >
                {localVerification.valid ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>
                      Independent Browser Audit: 100% Valid! Evaluated on your device. Every revealed member salt matched its commitment, and the deterministic permutation yielded identical flat assignments.
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                    <span>Audit failed: {localVerification.error}</span>
                  </div>
                )}
              </div>
            )}

            {/* Final Seed Card */}
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-navy-950 border border-gray-200 dark:border-navy-800 text-xs">
              <span className="text-gray-400 font-medium block mb-1">
                {t('lottery.combinedSeed')} (HMAC-SHA256):
              </span>
              <code className="block font-mono text-2xs break-all text-gray-800 dark:text-gray-200">
                {activeLottery.combinedSeed}
              </code>
            </div>

            {/* Allocation Roster Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-navy-800 text-gray-400 uppercase tracking-wider text-2xs font-semibold">
                    <th className="py-2.5 px-3"># Rank</th>
                    <th className="py-2.5 px-3">Member UID</th>
                    <th className="py-2.5 px-3">Allocated Flat</th>
                    <th className="py-2.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                  {(activeLottery.orderedAllocations || []).map((alloc: any) => {
                    const isSelf = user?.uid === alloc.memberId;
                    return (
                      <tr
                        key={alloc.rank}
                        className={`transition-colors ${
                          isSelf
                            ? 'bg-primary-50 dark:bg-primary-950/40 font-semibold text-primary-900 dark:text-primary-200'
                            : 'hover:bg-gray-50/50 dark:hover:bg-navy-800/50'
                        }`}
                      >
                        <td className="py-3 px-3 font-bold text-gray-500 dark:text-gray-400">
                          #{alloc.rank}
                        </td>
                        <td className="py-3 px-3 font-mono text-2xs">
                          {alloc.memberId} {isSelf && '(You)'}
                        </td>
                        <td className="py-3 px-3 font-bold text-primary-600 dark:text-primary-400 text-sm sm:text-base">
                          {alloc.flatNumber}
                        </td>
                        <td className="py-3 px-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                            Confirmed
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Staff Controls (Initiate / Reveal & Execute) */}
      {isStaff && (
        <div className="p-5 rounded-2xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 space-y-4">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
            <Lock className="w-4 h-4" />
            <span>Administrator & Treasurer Lottery Controls</span>
          </div>

          {!activeLottery || activeLottery.status === 'COMPLETED' ? (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-900 dark:text-white">
                Initiate New Lottery Phase
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Freeze active members and submit your 32-byte secret seed commitment:
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Admin SHA-256 Commitment (64 hex characters)"
                  value={adminCommitmentInput}
                  onChange={(e) => setAdminCommitmentInput(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl text-xs bg-white dark:bg-navy-900 border border-gray-300 dark:border-navy-700 font-mono"
                />
                <button
                  onClick={initiateNewLottery}
                  disabled={initiating || adminCommitmentInput.length !== 64}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white transition-colors disabled:opacity-50"
                >
                  {initiating ? 'Freezing...' : 'Freeze & Initiate'}
                </button>
              </div>
            </div>
          ) : activeLottery.status === 'COMMITMENT_PHASE' ? (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-900 dark:text-white">
                Execute Final Reveal & Draw
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Enter your original secret seed ($S_{'{admin}'}$) and member salts JSON to execute the draw:
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Revealed Admin Secret Seed"
                  value={adminSeedInput}
                  onChange={(e) => setAdminSeedInput(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-xs bg-white dark:bg-navy-900 border border-gray-300 dark:border-navy-700 font-mono"
                />
                <textarea
                  rows={4}
                  placeholder='Revealed Member Salts JSON: {"member_01": "salt...", ...}'
                  value={revealedSaltsJson}
                  onChange={(e) => setRevealedSaltsJson(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-xs bg-white dark:bg-navy-900 border border-gray-300 dark:border-navy-700 font-mono"
                />
                <button
                  onClick={executeLotteryDraw}
                  disabled={executing || !adminSeedInput}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-50"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>{executing ? 'Verifying & Executing...' : 'Verify Commitments & Execute Draw'}</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
